import { Box, Container, Typography, Card, Stack, Divider, Chip, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import { NAVY, NAVY_DARK, GREEN, GREEN_DARK, GREEN_LIGHT, PHONE, cutCornerTR } from '../theme';

export default function PrivacyPolicy() {
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Navbar />

      {/* Hero Header */}
      <Box sx={{
        pt: { xs: 14, md: 18 },
        pb: { xs: 6, md: 8 },
        bgcolor: NAVY_DARK,
        color: '#fff',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', top: -100, right: -100, width: 350, height: 350,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(GREEN, 0.2)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Button
            component={Link}
            to="/"
            startIcon={<ArrowBackIcon />}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              mb: 3,
              '&:hover': { color: GREEN_LIGHT, bgcolor: 'rgba(255,255,255,0.06)' },
            }}
          >
            Back to Home
          </Button>

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <Chip
              icon={<SecurityIcon sx={{ fontSize: 16, color: `${GREEN_LIGHT} !important` }} />}
              label="Legal & Data Protection"
              size="small"
              sx={{ bgcolor: alpha(GREEN, 0.15), color: GREEN_LIGHT, fontWeight: 700 }}
            />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>
              Last Updated: August 2026
            </Typography>
          </Stack>

          <Typography variant="h1" sx={{
            fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.4rem' },
            fontWeight: 900,
            mb: 2,
            color: '#fff',
          }}>
            Privacy Policy
          </Typography>

          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.75)', maxWidth: 720, lineHeight: 1.8 }}>
            This Privacy Policy explains how <strong>VALUEAID INFRASTRUCTURE PRIVATE LIMITED</strong> ("RideXCity", "we", "us", or "our")
            collects, uses, safeguards, and processes personal, financial, and vehicle telematics information when you invest in,
            partner with, or interact with our EV fleet ecosystem.
          </Typography>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 }, flex: 1 }}>
        <GridWrapper>
          <Card elevation={0} sx={{
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 3.5,
            border: `1.5px solid ${alpha(NAVY, 0.1)}`,
            bgcolor: '#ffffff',
          }}>
            <Stack spacing={4}>

              {/* Section 1 */}
              <Box>
                <Typography variant="h5" sx={{ color: NAVY, fontWeight: 800, mb: 1.5 }}>
                  1. Company Overview & Data Fiduciary
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
                  RideXCity is a green electric mobility brand wholly owned and operated by <strong>VALUEAID INFRASTRUCTURE PRIVATE LIMITED</strong>,
                  a company incorporated under the laws of India. We are committed to protecting your privacy in compliance with applicable Indian data
                  protection regulations, including the Digital Personal Data Protection Act, 2023 (DPDP Act) and the Information Technology Act, 2000.
                </Typography>
              </Box>

              <Divider />

              {/* Section 2 */}
              <Box>
                <Typography variant="h5" sx={{ color: NAVY, fontWeight: 800, mb: 1.5 }}>
                  2. Information We Collect
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
                  We collect information necessary to facilitate your electric vehicle fleet allocation, statutory KYC compliance, monthly return disbursements, and real-time asset tracking:
                </Typography>
                <Stack spacing={1.5} sx={{ pl: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    • <strong>Investor Identification & KYC Data:</strong> Full Name, Government Identity Proof (PAN Card, Aadhaar Card / Passport), Permanent Address, Contact Number, and Email Address.
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    • <strong>Financial & Bank Payout Details:</strong> Bank Account Number, IFSC Code, Account Holder Name, and GSTIN (where applicable) to disburse your fixed monthly fleet returns (e.g. ₹3,000/unit).
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    • <strong>Vehicle & IoT Telemetry Data:</strong> Real-time GPS location coordinates, vehicle speed, battery state of charge (SoC), charging cycles, swap station usage, odometer readings, and anti-theft sensor signals from your allocated EV units.
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    • <strong>Technical & Interaction Logs:</strong> Device identifiers, IP address, browser type, and enquiry form messages submitted through our website or WhatsApp desk.
                  </Typography>
                </Stack>
              </Box>

              <Divider />

              {/* Section 3 */}
              <Box>
                <Typography variant="h5" sx={{ color: NAVY, fontWeight: 800, mb: 1.5 }}>
                  3. How We Use Your Information
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
                  Your data is used strictly for legitimate commercial, operational, and regulatory purposes:
                </Typography>
                <Stack spacing={1.5} sx={{ pl: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    • <strong>Fleet Deployment & Allocation:</strong> Registering, procuring, and allocating commercial electric two-wheelers, IoT hardware, and batteries under your selected investment plan.
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    • <strong>Disbursing Monthly Returns:</strong> Processing prompt, scheduled monthly return payments directly into your designated bank account and maintaining accounting ledgers.
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    • <strong>Asset Protection & Fleet Security:</strong> Monitoring EV health, preventive maintenance, geo-fencing, speed governance, and rapid recovery in case of unauthorized movement.
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    • <strong>Taxation & Statutory Compliance:</strong> Filing TDS certificates, issuing GST invoices, and adhering to financial audits mandated by Indian authorities.
                  </Typography>
                </Stack>
              </Box>

              <Divider />

              {/* Section 4 */}
              <Box>
                <Typography variant="h5" sx={{ color: NAVY, fontWeight: 800, mb: 1.5 }}>
                  4. IoT & GPS Telemetry Data Governance
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  All electric two-wheelers in the RideXCity ecosystem are fitted with 4G IoT telematics and remote diagnostics.
                  Telemetry data is collected continuously to ensure fleet efficiency, rider safety, battery lifecycle monitoring, and commercial optimization.
                  This telemetry data is securely stored in cloud infrastructure located within India.
                </Typography>
              </Box>

              <Divider />

              {/* Section 5 */}
              <Box>
                <Typography variant="h5" sx={{ color: NAVY, fontWeight: 800, mb: 1.5 }}>
                  5. Data Sharing & Third-Party Disclosures
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
                  We do not sell, trade, or rent your personal information. We may share necessary data only with:
                </Typography>
                <Stack spacing={1.5} sx={{ pl: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    • <strong>Banking & Escrow Partners:</strong> Scheduled commercial banks in India to process direct NEFT/RTGS/IMPS payouts.
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    • <strong>Insurance & Roadside Assistance Providers:</strong> IRDAI-registered insurance companies for asset coverage, claims settlement, and third-party liabilities.
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    • <strong>Legal & Statutory Authorities:</strong> When required by court order, law enforcement, or applicable statutory mandates.
                  </Typography>
                </Stack>
              </Box>

              <Divider />

              {/* Section 6 */}
              <Box>
                <Typography variant="h5" sx={{ color: NAVY, fontWeight: 800, mb: 1.5 }}>
                  6. Data Security & Storage
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  We deploy industry-standard 256-bit SSL encryption, strict role-based access controls, and audited cloud data centers
                  to protect your personal, financial, and telemetry records from unauthorized access, breach, alteration, or disclosure.
                </Typography>
              </Box>

              <Divider />

              {/* Section 7 - Grievance Officer */}
              <Box sx={{
                ...cutCornerTR(16),
                p: 3.5,
                bgcolor: alpha(NAVY, 0.04),
                border: `1.5px solid ${alpha(NAVY, 0.15)}`,
              }}>
                <Typography variant="h6" sx={{ color: NAVY, fontWeight: 800, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VerifiedUserIcon sx={{ color: GREEN_DARK }} /> 7. Grievance Officer & Contact Information
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
                  In accordance with the Information Technology Act, 2000 and the Digital Personal Data Protection Act, 2023,
                  the details of the Grievance Officer are provided below:
                </Typography>
                <Stack spacing={1.2}>
                  <Typography variant="body2" sx={{ color: NAVY, fontWeight: 700 }}>
                    Company: VALUEAID INFRASTRUCTURE PRIVATE LIMITED (RideXCity)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon sx={{ fontSize: 18, color: GREEN_DARK }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Email: <strong>investor@ridexcity.com</strong> / <strong>support@ridexcity.com</strong>
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon sx={{ fontSize: 18, color: GREEN_DARK }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Phone / WhatsApp: <strong>{PHONE}</strong>
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <LocationOnIcon sx={{ fontSize: 18, color: GREEN_DARK, mt: 0.3 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Official Website: <strong>www.ridexcity.com</strong> · Made in India 🇮🇳
                    </Typography>
                  </Box>
                </Stack>
              </Box>

            </Stack>
          </Card>
        </GridWrapper>
      </Container>

      <Footer />
      <FloatingActions />
    </Box>
  );
}

function GridWrapper({ children }) {
  return (
    <Box sx={{ maxWidth: 960, mx: 'auto' }}>
      {children}
    </Box>
  );
}
